import { lazy, ComponentType } from 'react';

/**
 * Lazy load a component with automatic retry on chunk loading errors.
 * When a new deployment happens, old cached chunks may fail to load.
 * This utility auto-reloads the page once to fetch fresh assets.
 */
export function lazyWithRetry<T extends object>(factory: () => Promise<{ default: ComponentType<T> }>) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (error: any) {
      const message = String(error?.message || error);
      const isChunkError =
        message.includes('Failed to fetch dynamically imported module') ||
        message.includes('Importing a module script failed') ||
        message.includes('ChunkLoadError') ||
        message.includes('Loading chunk') ||
        message.includes('error loading dynamically imported module');

      if (isChunkError && typeof window !== 'undefined') {
        const hasReloaded = sessionStorage.getItem('chunk-reload') === '1';
        
        if (!hasReloaded) {
          try {
            sessionStorage.setItem('chunk-reload', '1');
            // Force a hard reload bypassing cache
            const url = new URL(window.location.href);
            url.searchParams.set('v', Date.now().toString());
            window.location.replace(url.toString());
          } catch (_) {
            window.location.reload();
          }
          // Return a never-resolving promise to prevent the error from propagating
          // while the page is reloading
          return new Promise(() => {});
        }
      }

      throw error;
    }
  });
}
